import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { EnterpriseArchitectureDiagram } from "@/components/EnterpriseArchitectureDiagram";
import { CICDTemplates } from "@/components/CICDTemplates";
import { 
  ShieldCheck, ChevronRight, ChevronLeft, Loader2, CheckCircle2, AlertTriangle, 
  XCircle, FileText, Box, DollarSign, Calendar, RefreshCw, Sparkles, GitBranch
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Phase3Review = ({ project, onUpdateProject, onNext, onBack }) => {
  const { token } = useAuth();
  const [architecture, setArchitecture] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const pollingRef = useRef(null);

  useEffect(() => {
    if (project?.phase3?.executive_summary) {
      setArchitecture(project.phase3);
    } else {
      generateArchitecture();
    }
    
    // Cleanup polling on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [project?.id]);

  const pollTaskStatus = async (taskId) => {
    try {
      const response = await axios.get(`${API}/ai/architecture/status/${taskId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const { status, progress: taskProgress, result, error: taskError } = response.data;
      
      setProgress(taskProgress || 0);
      
      if (status === "processing") {
        if (taskProgress < 30) {
          setStatusMessage("Analyzing project requirements...");
        } else if (taskProgress < 70) {
          setStatusMessage("AI is designing your cloud infrastructure...");
        } else {
          setStatusMessage("Finalizing architecture and saving...");
        }
      }
      
      if (status === "completed" && result) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setArchitecture(result);
        onUpdateProject({
          ...project,
          phase3: result,
          latest_arch_version: result.version,
          current_phase: 3
        });
        setGenerating(false);
        setProgress(100);
        toast.success(`Architecture generated! (Version ${result.version || 1})`);
      } else if (status === "failed") {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setError(taskError || "Architecture generation failed");
        setGenerating(false);
        toast.error(taskError || "Architecture generation failed");
      }
    } catch (err) {
      console.error("Error polling task status:", err);
    }
  };

  const generateArchitecture = async () => {
    try {
      setGenerating(true);
      setError(null);
      setProgress(0);
      setStatusMessage("Starting architecture generation...");
      
      // Start async generation
      const response = await axios.post(`${API}/ai/architecture/start`, {
        project_id: project.id
      }, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      const { task_id } = response.data;
      
      if (task_id) {
        // Poll for status every 2 seconds
        pollingRef.current = setInterval(() => pollTaskStatus(task_id), 2000);
        // Initial poll
        pollTaskStatus(task_id);
      } else {
        throw new Error("No task ID returned");
      }
    } catch (err) {
      let errorMsg = "Failed to start architecture generation. Please try again.";
      if (err.response?.status === 403) {
        errorMsg = "You don't have access to this project";
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      setError(errorMsg);
      setGenerating(false);
      toast.error(errorMsg);
    }
  };

  const auditResults = project?.audit_results || {};
  const criticalCount = auditResults.compliance?.critical_count || 0;
  const [acknowledgedIssues, setAcknowledgedIssues] = useState(false);
  const canProceed = criticalCount === 0 || acknowledgedIssues;

  return (
    <div className="animate-fade-in">
      <div className="mb-6 md:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-sm bg-[#00f0ff]/10 border border-[#00f0ff]/30 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#00f0ff]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Phase 3: Review & Fix</h1>
            <p className="text-sm md:text-base text-zinc-500">Review architecture and address audit findings</p>
          </div>
        </div>
      </div>

      {/* Audit Status Banner */}
      <Card className={`mb-6 ${canProceed ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {canProceed ? (
                <>
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                  <div>
                    <h3 className="text-white font-semibold">
                      {acknowledgedIssues ? 'Issues Acknowledged - Proceed with Caution' : 'All Critical Checks Passed'}
                    </h3>
                    <p className="text-sm text-zinc-400">
                      {acknowledgedIssues ? 'You have acknowledged the critical issues and can proceed' : 'Your architecture is ready for certified export'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="w-8 h-8 text-red-500" />
                  <div>
                    <h3 className="text-white font-semibold">{criticalCount} Critical Issues Found</h3>
                    <p className="text-sm text-zinc-400">Address critical findings before export is allowed</p>
                  </div>
                </>
              )}
            </div>
            {criticalCount > 0 && !acknowledgedIssues && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setAcknowledgedIssues(true);
                  toast.info("Issues acknowledged. You can now proceed, but please address these issues before production deployment.");
                }}
                className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10 hover:text-yellow-400"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Acknowledge & Proceed
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {generating ? (
        <Card className="bg-[#09090b] border-white/5">
          <CardContent className="py-20 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-[#00f0ff]/10 border border-[#00f0ff]/30 flex items-center justify-center mb-6">
              <Loader2 className="w-8 h-8 text-[#00f0ff] animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Generating Architecture</h3>
            <p className="text-zinc-500 text-center max-w-md mb-4">
              {statusMessage || "Creating your audit-ready architecture design..."}
            </p>
            <div className="w-64 mb-2">
              <Progress value={progress} className="h-2" />
            </div>
            <p className="text-zinc-600 text-sm">{progress}% complete</p>
          </CardContent>
        </Card>
      ) : !architecture ? (
        <Card className="bg-[#09090b] border-white/5">
          <CardContent className="py-20 flex flex-col items-center justify-center">
            {error ? (
              <>
                <div className="w-16 h-16 rounded-sm bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Generation Failed</h3>
                <p className="text-zinc-500 text-center max-w-md mb-4">{error}</p>
                <p className="text-zinc-600 text-xs text-center mb-6 max-w-sm">
                  This can happen due to high traffic. Please wait a moment and try again.
                </p>
                <Button onClick={generateArchitecture} className="bg-[#00f0ff] text-black rounded-sm hover:bg-[#00f0ff]/90">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Generation
                </Button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-6">
                  <Box className="w-8 h-8 text-zinc-600" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Generate Architecture</h3>
                <p className="text-zinc-500 mb-6">Create your architecture based on audit results</p>
                <Button onClick={generateArchitecture} className="bg-[#00f0ff] text-black rounded-sm hover:bg-[#00f0ff]/90">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Architecture
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900/50 border border-white/5 p-1 flex-wrap h-auto">
            <TabsTrigger value="summary" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black text-xs md:text-sm">
              <FileText className="w-4 h-4 mr-1 md:mr-2" />
              Summary
            </TabsTrigger>
            <TabsTrigger value="diagram" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black text-xs md:text-sm">
              <Box className="w-4 h-4 mr-1 md:mr-2" />
              Diagram
            </TabsTrigger>
            <TabsTrigger value="cost" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black text-xs md:text-sm">
              <DollarSign className="w-4 h-4 mr-1 md:mr-2" />
              Cost
            </TabsTrigger>
            <TabsTrigger value="cicd" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black text-xs md:text-sm">
              <GitBranch className="w-4 h-4 mr-1 md:mr-2" />
              CI/CD
            </TabsTrigger>
            <TabsTrigger value="roadmap" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black text-xs md:text-sm">
              <Calendar className="w-4 h-4 mr-1 md:mr-2" />
              Roadmap
            </TabsTrigger>
          </TabsList>

          <TabsContent value="summary">
            <Card className="bg-[#09090b] border-white/5">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#00f0ff]" />
                  Executive Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] md:h-[500px] pr-4">
                  <div className="prose prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap font-sans text-zinc-300 text-sm leading-relaxed bg-transparent border-0 p-0">
                      {architecture.executive_summary || "No summary generated"}
                    </pre>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="diagram">
            <EnterpriseArchitectureDiagram 
              architectureData={architecture}
              cloudPlatform={project?.phase1?.cloud_platform || "AWS"}
            />
          </TabsContent>

          <TabsContent value="cicd">
            <CICDTemplates 
              onSelectTemplate={(template) => {
                toast.success(`Selected ${template.name} - Copy or download from the template dialog`);
              }}
            />
          </TabsContent>

          <TabsContent value="cost">
            <Card className="bg-[#09090b] border-white/5">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Cost Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] md:h-[500px] pr-4">
                  <pre className="whitespace-pre-wrap font-sans text-zinc-300 text-sm leading-relaxed">
                    {architecture.cost_analysis || "No cost analysis generated"}
                  </pre>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="roadmap">
            <Card className="bg-[#09090b] border-white/5">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#00f0ff]" />
                  Implementation Roadmap
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] md:h-[500px] pr-4">
                  <pre className="whitespace-pre-wrap font-sans text-zinc-300 text-sm leading-relaxed">
                    {architecture.roadmap || "No roadmap generated"}
                  </pre>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Regenerate Button */}
      {architecture && (
        <div className="flex justify-center mt-6">
          <Button
            variant="outline"
            onClick={generateArchitecture}
            className="border-zinc-700 text-zinc-400 hover:bg-white/5"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Regenerate Architecture
          </Button>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-zinc-700 text-zinc-300 hover:bg-white/5 rounded-sm"
          data-testid="phase3-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Audit
        </Button>

        <div className="flex items-center gap-4">
          {!canProceed && criticalCount > 0 && (
            <span className="text-sm text-yellow-500">
              Acknowledge {criticalCount} issue{criticalCount > 1 ? 's' : ''} above to proceed
            </span>
          )}
          {!architecture && canProceed && (
            <span className="text-sm text-zinc-500">Generate architecture first</span>
          )}
          <Button
            onClick={onNext}
            disabled={!architecture || !canProceed}
            className="bg-[#00f0ff] text-black font-semibold hover:bg-[#00f0ff]/90 hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] rounded-sm px-6 disabled:opacity-50"
            data-testid="phase3-next-btn"
          >
            Export Certified IaC
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};
